/**
 * Per-user UI preferences context (API schema 110) — the store behind list page sizes and
 * visible-column layouts.
 *
 * Provider placement (R-18.200): inside `AuthProvider` (it needs to know whether there IS a
 * caller, and its query must not fire on the login screen) and outside `RouterProvider` (list
 * pages read it). See App.tsx.
 *
 * It BLOCKS the first render until the preference set has settled, the same way the auth probe
 * does. That is deliberate: a list rendered before its saved page size is known would fetch 50
 * rows and then immediately re-fetch 100, doubling the request on every page load and flashing
 * the wrong pager. One small request, already behind the auth gate, buys every list a correct
 * first render with no per-page `enabled` plumbing.
 *
 * Writes are optimistic + debounced (write-behind): the cache updates on the spot so the table
 * re-renders immediately, and the PUT follows once the user stops clicking. The mutation opts out
 * of the global invalidate-all (query-client.ts) — a page-size click must not invalidate every
 * cached read in the app — and out of the global error toast: a preference that fails to persist
 * costs the user nothing this session and nothing they can act on, exactly as the localStorage
 * quota failure it replaces was swallowed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { fetchUserSettings, putUserSetting } from './user-settings-api';

import type { UserSettingsMap, UserSettingValue } from './user-settings-api';
import type { ReactNode } from 'react';

import { isMfaPending, useAuth } from '@/shared/auth/AuthProvider';

/** The TanStack Query key for the caller's preference set. */
export const USER_SETTINGS_QUERY_KEY = ['user-settings'] as const;

/**
 * How long to wait after the last change before persisting. Long enough to collapse a burst
 * (dragging a column, clicking through page sizes) into one request, short enough that a user
 * who changes a setting and immediately closes the tab keeps it.
 */
const WRITE_DEBOUNCE_MS = 400;

export interface UserSettingsContextValue {
  /** Every stored preference, keyed by setting key. Empty until/unless something is saved. */
  settings: UserSettingsMap;
  /**
   * Merge a patch into one preference. MERGE, not replace: writing `{ pageSize }` leaves a saved
   * `columns` untouched, which is why the two independent controls on a list can share one key.
   */
  patchSetting: (key: string, patch: UserSettingValue) => void;
}

const EMPTY_SETTINGS: UserSettingsMap = {};

// Exported so the test harness (`@/test/render`) can supply a fixed preference context for
// component tests without standing up the real query; app code uses `useUserSettings`.
export const UserSettingsContext = createContext<UserSettingsContextValue | null>(null);

export function UserSettingsProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { isAuthenticated, user } = useAuth();
  // A session held on the mandatory-2FA screen (plan R2 §6) may only call /auth/me and the two
  // enrolment steps; anything else is a 403 MFA_REQUIRED. This provider sits OUTSIDE the route
  // guard, so it waits for the code-verified session instead of asking and being refused.
  const canLoad = isAuthenticated && !isMfaPending(user);
  const queryClient = useQueryClient();
  // One pending write per key, so changing the vehicles page size does not cancel a pending
  // customers column save.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const { data: settings = EMPTY_SETTINGS, isLoading } = useQuery({
    queryKey: USER_SETTINGS_QUERY_KEY,
    queryFn: fetchUserSettings,
    enabled: canLoad,
    // This client is the only writer, and it updates the cache itself on every write — there is
    // nothing to go stale between sessions.
    staleTime: Infinity,
    retry: false,
    meta: { suppressGlobalError: true },
  });

  const writeMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: UserSettingValue }) =>
      putUserSetting(key, value),
    meta: { skipGlobalInvalidation: true, suppressGlobalError: true },
  });

  // Drop pending writes on teardown (logout / unmount) so a timer cannot fire against a torn-down
  // client. `mutate` is fire-and-forget, so nothing is left awaiting either.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const { mutate } = writeMutation;
  const patchSetting = useCallback(
    (key: string, patch: UserSettingValue) => {
      const current = queryClient.getQueryData<UserSettingsMap>(USER_SETTINGS_QUERY_KEY) ?? {};
      // The PUT replaces the key's whole value, so merge here and send the result.
      const merged = { ...(current[key] ?? {}), ...patch };
      queryClient.setQueryData<UserSettingsMap>(USER_SETTINGS_QUERY_KEY, {
        ...current,
        [key]: merged,
      });

      const existing = timers.current.get(key);
      if (existing !== undefined) clearTimeout(existing);
      timers.current.set(
        key,
        setTimeout(() => {
          timers.current.delete(key);
          mutate({ key, value: merged });
        }, WRITE_DEBOUNCE_MS),
      );
    },
    [queryClient, mutate],
  );

  const value = useMemo<UserSettingsContextValue>(
    () => ({ settings, patchSetting }),
    [settings, patchSetting],
  );

  // Only an authenticated visitor waits — the login screen has no preferences to load.
  if (canLoad && isLoading) {
    return <Spin fullscreen />;
  }

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}

/**
 * Read the preference context. Throws outside `<UserSettingsProvider>` so a misplacement is a
 * loud failure, not a silently unsaved preference.
 */
export function useUserSettings(): UserSettingsContextValue {
  const context = useContext(UserSettingsContext);
  if (context === null) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
}
